import { useEffect, useState, useCallback, useRef } from 'react'
import { useStore } from './useStore'

const INACTIVITY_TIMEOUT = 13 * 60 * 1000 // 13 minutes
const WARNING_DURATION = 2 * 60 // 2 minutes

export const useInactivityWarning = () => {
  const [showWarning, setShowWarning] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(WARNING_DURATION)
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const store = useStore()
  const { auth } = store

  const resetInactivityTimer = useCallback(() => {
    if (!auth.is_authenticated) return

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }

    // Set new timer
    inactivityTimerRef.current = setTimeout(() => {
      setShowWarning(true)
      setSecondsRemaining(WARNING_DURATION)
    }, INACTIVITY_TIMEOUT)
  }, [auth.is_authenticated])

  const extendSession = useCallback(async () => {
    // Make a lightweight API request to refresh the token
    // The axios interceptor will automatically handle token refresh
    try {
      await store.api.user.whoAmI()

      // Hide warning and reset timers
      setShowWarning(false)
      setSecondsRemaining(WARNING_DURATION)
      lastActivityRef.current = Date.now() // Reset activity tracking
      resetInactivityTimer()
    } catch (error) {
      // If the request fails, user is likely already logged out
      // Force logout
      setShowWarning(false)
      auth.logout(true)
    }
  }, [store.api.user, resetInactivityTimer, auth])

  const handleLogout = useCallback(() => {
    setShowWarning(false)
    auth.logout(true)
  }, [auth])

  // Countdown when warning is shown
  useEffect(() => {
    // Don't run countdown if user is not authenticated
    if (!auth.is_authenticated || !showWarning) {
      // Clear countdown interval when warning is hidden or user logged out
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
      return
    }

    // Start the countdown
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          // Time's up, logout
          // Use setTimeout to avoid state updates during render
          setTimeout(() => {
            setShowWarning(false)
            auth.logout(true)
          }, 0)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [auth, showWarning])

  // Track user activity
  useEffect(() => {
    if (!auth.is_authenticated) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      return
    }

    // Don't track activity when warning is shown - user must explicitly click a button
    if (showWarning) {
      return
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']

    const handleActivity = () => {
      // Throttle: only reset timer once per second to prevent excessive calls on mousemove
      const now = Date.now()
      if (now - lastActivityRef.current < 1000) {
        return
      }
      lastActivityRef.current = now
      resetInactivityTimer()
    }

    // Start initial timer
    resetInactivityTimer()

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity)
    })

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [auth.is_authenticated, showWarning, resetInactivityTimer])

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [])

  return {
    showWarning,
    secondsRemaining,
    extendSession,
    handleLogout,
  }
}
